<template>
  <UsageExample v-model="tab" :code="code" :name="name" :tabs="exampleTabs">
    <div class="demo-container">
      <!-- Basic Usage -->
      <section>
        <h4>Basic Usage</h4>
        <hx-tabs v-model="selectedTab">
          <hx-tab value="tab1" title="Tab 1" />
          <hx-tab value="tab2" title="Tab 2" />
        </hx-tabs>
      </section>

      <!-- Props -->
      <section>
        <h4>Different Variants based on props</h4>
        <hx-tabs v-model="selectedTab2">
          <hx-tab value="icon" title="With Icon" icon="mdi-home" />
          <hx-tab value="badge" title="With Badge" badge />
          <hx-tab value="disabled" title="Disabled" disabled />
          <template #content>
            <hx-tab-content value="icon">Icon Tab Content</hx-tab-content>
            <hx-tab-content value="badge">Badge Tab Content</hx-tab-content>
            <hx-tab-content value="disabled">Disabled Tab Content</hx-tab-content>
          </template>
        </hx-tabs>

        <div class="my-5"></div>

        <hx-tabs
          v-model="selectedTab3"
          :align-tabs="alignTabs"
          :grow="grow"
          :show-content="showContent"
        >
          <hx-tab value="overview" title="Overview" @click="onTabClick" />
          <hx-tab value="details" title="Details" @click="onTabClick" />
          <hx-tab value="settings" title="Settings" icon="mdi-cog" @click="onTabClick" />
          <hx-tab value="orders" title="Orders" badge @click="onTabClick" />
          <hx-tab
            value="disabled"
            title="Disabled Tab"
            icon="mdi-plus-circle-outline"
            disabled
            @click="onTabClick"
          />

          <template #content>
            <hx-tab-content value="overview">
              <div>
                <div class="text-black">Overview</div>
                <p>
                  This is the overview content. The tab component follows Helix UI design
                  specifications.
                </p>
              </div>
            </hx-tab-content>

            <hx-tab-content value="details">
              <div>
                <div class="text-black">Details</div>
                <p>This tab shows details about the item. You can add any content here.</p>
              </div>
            </hx-tab-content>

            <hx-tab-content value="settings">
              <div>
                <div class="text-black">Settings</div>
                <p>
                  This tab has an icon and contains settings. Notice how the icon appears before the
                  title.
                </p>
              </div>
            </hx-tab-content>

            <hx-tab-content value="orders">
              <div>
                <div class="text-black">Orders</div>
                <p>This tab has a notification badge. The badge is shown as a small red circle.</p>
              </div>
            </hx-tab-content>

            <hx-tab-content value="disabled">
              <div>
                <div class="text-black">Disabled</div>
                <p>This content shouldn't be visible because the tab is disabled.</p>
              </div>
            </hx-tab-content>
          </template>
        </hx-tabs>
      </section>

      <section>
        <h4>Overflowed Tabs</h4>
        <hx-tabs v-model="selectedTab4" show-arrows>
          <hx-tab
            v-for="i in 30"
            :key="i"
            :value="`tab-${i}`"
            :title="`Very Long Tab Title ${i}`"
            :icon="availableIcons[(i - 1) % availableIcons.length]"
            :disabled="i % 4 === 0"
            :badge="i % 3 === 0"
            @click="onTabClick"
          />
          <template #content>
            <hx-tab-content v-for="i in 30" :key="i" :value="`tab-${i}`">
              <div class="px-13 py-4">
                <p>Content for tab {{ i }}</p>
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor
                  incididunt ut labore et dolore magna aliqua.
                </p>
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor
                  incididunt ut labore et dolore magna aliqua. Lorem ipsum dolor sit amet,
                  consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore
                  magna aliqua.
                </p>
              </div>
            </hx-tab-content>
          </template>
        </hx-tabs>
      </section>
    </div>

    <template #configuration>
      <v-row>
        <v-col cols="12">
          <v-row>
            <v-col cols="12">
              <hx-checkbox v-model="grow" label="Grow tabs to fill space"></hx-checkbox>
            </v-col>
            <v-col cols="12">
              <hx-checkbox v-model="showContent" label="Show content panel"></hx-checkbox>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="alignTabs"
                :items="['start', 'center', 'end']"
                label="Tab alignment"
                density="compact"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="selectedTab3"
                :items="['overview', 'details', 'settings', 'orders', 'disabled']"
                label="Selected tab"
                density="compact"
              ></v-select>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </UsageExample>
</template>

<script>
import UsageExample from '@/components/helix/utils/UsageExample';

export default {
  name: 'HelixTabs',
  components: { UsageExample },
  setup() {
    const availableIcons = [
      'mdi-lock',
      'mdi-star',
      'mdi-heart',
      'mdi-account',
      'mdi-home',
      'mdi-email',
      'mdi-phone',
      'mdi-camera',
      'mdi-music',
      'mdi-palette',
      'mdi-cart',
      'mdi-bell',
      'mdi-message',
      'mdi-calendar',
      'mdi-clock',
      'mdi-map-marker',
      'mdi-cog',
      'mdi-help-circle',
      'mdi-information',
      'mdi-alert',
      'mdi-check-circle',
      'mdi-access-point',
      'mdi-plus-circle-outline',
      'mdi-minus-circle-outline',
      'mdi-close-circle-outline',
      'mdi-check-circle-outline'
    ];

    return {
      availableIcons
    };
  },
  data() {
    return {
      name: 'hx-tabs',
      tab: 'Tabs Usage',
      exampleTabs: ['Tabs Usage'],

      alignTabs: 'start',
      grow: false,
      showContent: true,

      selectedTab: 'tab1',
      selectedTab2: 'icon',
      selectedTab3: 'overview',
      selectedTab4: 'tab-1'
    };
  },
  computed: {
    code() {
      return `<hx-tabs
  v-model="selectedTab"
  :align-tabs="${this.alignTabs}"
  :grow="${this.grow}"
  :show-content="${this.showContent}"
>
  <hx-tab value="overview" title="Overview" @click="onTabClick" />
  <hx-tab value="details" title="Details" @click="onTabClick" />
  <hx-tab value="settings" title="Settings" icon="mdi-cog" @click="onTabClick" />
  <hx-tab value="orders" title="Orders" badge @click="onTabClick" />
  <hx-tab value="disabled" title="Disabled Tab" disabled @click="onTabClick" />
  
  <!-- This is optional -->
  <template #content>
    <hx-tab-content value="overview">
      <div>
        <b>Overview</b>
        <p>This is the overview content.</p>
      </div>
    </hx-tab-content>
    
    <hx-tab-content value="details">
      <!-- Details content -->
    </hx-tab-content>
  </template>
</hx-tabs>`;
    }
  },
  methods: {
    onTabClick(tab) {
      console.log('Tab clicked:', tab);
    }
  }
};
</script>

<style lang="scss" scoped>
.demo-container {
  padding: 20px;
  background-color: #f4f5f6;

  section {
    margin-bottom: 40px;
  }
}
</style>
